
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import UpdateButton from '@/components/UpdateButton.vue';
import {
  Connection, Lead, PermissionCode,
} from '@/types';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import { getLeadCustomValueDate, getLeadCustomValueText, currentUserHasPermission } from '@/helpers';
import PracticeNotesDialog from '@/components/PracticeNotesDialog.vue';

moment.locale('it');

@Component({
  components: {
    UpdateButton,
    DatePickerDialog,
    PracticeNotesDialog,
  },
})
export default class EditIterConfirmationDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;

  dialog = false
  valid = false

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  loadingNotes = false
  practiceNotesDialog = false

  iterConfirmation: string | null = null
  iterConfirmationPendingDate: Date | null = null
  iterConfirmationDate: Date | null = null

  // Questo componente necessita che la Lead a cui è associato l'allegato sia selezionata

  hasPermission(codename: PermissionCode): boolean { return currentUserHasPermission(codename); }

  // La Connection selezionata
  get connection(): Connection | null {
    return this.$store.getters.currentConnection as Connection | null;
  }

  // La Lead selezionata (associata alla Connection)
  get lead(): Lead | null {
    return this.$store.getters.currentLead as Lead | null;
  }

  get practiceNotes(): string {
    return getLeadCustomValueText(this.lead, 'practiceNotes') ?? '';
  }

  // La Connection è in caricamento?
  get loading(): boolean {
    return this.$store.getters.isConnectionLoading;
  }

  // La Connection è in aggiornamento?
  get updating(): boolean {
    return this.$store.getters.isConnectionUpdating;
  }

  // È necessario salvare le modifiche alla Connection?
  get dirty(): boolean {
    return this.$store.getters.isConnectionDirty;
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Connection/Lead selezionata
  refresh() {
    console.log(`${this.constructor.name}: refresh()`); // eslint-disable-line no-console

    // Aggiorna i campi con i valori della Connection
    this.iterConfirmation = (getLeadCustomValueText(this.lead, 'iterConfirmation') ?? null);
    this.iterConfirmationDate = (getLeadCustomValueDate(this.lead, 'iterConfirmationDate') ?? null);
    this.iterConfirmationPendingDate = (getLeadCustomValueDate(this.lead, 'iterConfirmationPendingDate') ?? null);
  }

  onClickDate(name: string) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    const prevDate = name in this && this[name]
      ? moment(this[name]).toISOString(true).substr(0, 10)
      : null;
    this.datePickerDate = prevDate;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      this[name] = null;
      update[name] = null;
    }

    // Modifico eventuali campi collegati
    switch (name) {
      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName in this) {
      this[this.datePickerName] = selectedDate;
      update[this.datePickerName] = this[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  onFieldUpdated(field: string, value: unknown = undefined) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    if (value !== undefined) this[field] = value;

    const update = {} as { [key: string]: unknown };

    update[field] = this[field] ?? '';

    const form = this.getForm();

    switch (field) {
      case 'iterConfirmation':
        if (this.iterConfirmation === 'pending') {
          this.iterConfirmationPendingDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.iterConfirmationPendingDate = this.iterConfirmationPendingDate;
          // Cancello eventuali date di conferma
          this.iterConfirmationDate = null;
          update.iterConfirmationDate = null;
        } else if (this.iterConfirmation === 'confirmed') {
          this.iterConfirmationDate = moment(moment(), 'YYYY-MM-DD').toDate();
          update.iterConfirmationDate = this.iterConfirmationDate;
        } else {
          this.iterConfirmationPendingDate = null;
          update.iterConfirmationPendingDate = null;
          this.iterConfirmationDate = null;
          update.iterConfirmationDate = null;
        }

        if (form) form.validate();

        break;

      default:
        break;
    }

    this.$store.dispatch('updateConnectionFields', {
      fields: update,
    });
  }

  @Watch('value')
  onValueChange(val: boolean) {
    this.dialog = val;

    // Se stiamo aprendo il dialog passa alla prima scheda
    if (val) {
      // Inizializza tutti i campi del modulo
      this.refresh();
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }

    // Se stiamo chiudendo il dialog, deseleziona la Connection
    if (!val) {
      this.$store.dispatch('unselectConnection');
    }
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  // Ritorna `true` se i campi del modulo devono essere per qualche motivo tutti disattivati
  get fieldsDisabled(): boolean {
    return this.lead?.status !== 'open'
      || this.loading
      || this.updating;
  }

  get isIterConfirmationPendingDateValid(): boolean {
    // Se la conferma è in sospeso la data di sospensione deve essere presente
    return this.iterConfirmation !== 'pending' || !!this.iterConfirmationPendingDate;
  }

  getForm(): (Vue & { validate: () => boolean; reset: () => boolean }) | undefined {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  async onShow() {
    // Non permette l'apertura del dialog durante il caricamento
    if (this.loadingNotes) return;

    console.log(`${this.constructor.name}: onShow()`); // eslint-disable-line no-console

    // Se l'utente non ha il permesso per mostrare o modificare, si ferma.
    if (!this.hasPermission('practiceNotes.edit') && !this.hasPermission('practiceNotes.show')) return;

    this.loadingNotes = true;
    if (this.connection) {
      // Seleziona la Connection prima di aprire il dialog
      await this.$store.dispatch('selectConnection', { id: this.connection.id });
    }
    this.practiceNotesDialog = true;
    this.loadingNotes = false;
  }

  onCancel() {
    this.dialog = false;

    this.$emit('cancel');
  }

  async onSave() {
    console.log(`${this.constructor.name}: onSave()`); // eslint-disable-line no-console

    await this.$store.dispatch('updateSelectedConnection');

    this.dialog = false;
  }
}
